import React, {useMemo} from "react";
import {
    Avatar,
    Button,
    Card,
    Col,
    Grid,
    List,
    Row,
    Space,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import {useNavigate} from "react-router-dom";
import {
    BellOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    FileTextOutlined,
    ReloadOutlined,
    SendOutlined,
    SmileOutlined,
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";

import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";

import {
    Bar,
    BarChart,
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from "recharts";

import {
    useOperatorOpenChats,
    useOperatorAverageRating,
    useOperatorAverageWaitTime,
    useOperatorUnreadChats,
    useOperatorActiveChats,
    useOperatorResolvedChats,
} from "./operator-dashboard-home-query";
import {useQueryClient} from "@tanstack/react-query";
import {stateMeta} from "../../../utils/chatState";

const {Text} = Typography;
const {useBreakpoint} = Grid;

// ---------- helpers (تاریخ و عدد فارسی) ----------
const toPersianDateObject = (d /* string | Date | null */) =>
    d ? new DateObject({date: new Date(d), calendar: persian, locale: persian_fa}) : null;

const fmtPersian = (d /* string|Date|null */, token = "YYYY/MM/DD HH:mm:ss") => {
    const obj = toPersianDateObject(d);
    return obj ? obj.format(token) : "-";
};

const enToFaDigits = (v) => String(v ?? "").replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);

const faDate = (d) => fmtPersian(d, "MM/DD");
const faDateWithHour = (d) => fmtPersian(d, "HH:mm YYYY/MM/DD");

const getInitials = (name = "") => {
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
};

const safeNumber = (v) => (typeof v === "number" && !isNaN(v) ? v : 0);

const formatDecimalFa = (v, digits = 2) => {
    const num = safeNumber(v);
    return enToFaDigits(num.toFixed(digits));
};

const roundTo = (v, digits = 2) => {
    const num = safeNumber(v);
    const factor = Math.pow(10, digits);
    return Math.round(num * factor) / factor;
};

export default function OperatorDashboardHome() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const queryClient = useQueryClient();

    // ----- API calls (startDate رو خالی گذاشتیم تا بک‌اند دیفالت ۷ روز رو خودش حساب کند) -----
    const {data: openChatsRes} = useOperatorOpenChats(undefined);
    const {data: avgRatingRes} = useOperatorAverageRating(undefined);
    const {data: avgWaitRes} = useOperatorAverageWaitTime(undefined);
    const {data: unreadRes} = useOperatorUnreadChats();
    const {data: activeChatsRes} = useOperatorActiveChats();
    const {data: resolvedChatsRes} = useOperatorResolvedChats(undefined);

    const openChatsDto = openChatsRes;
    const avgRatingDto = avgRatingRes;
    const avgWaitDto = avgWaitRes;
    const unreadCount = unreadRes?.unreadChatsCount ?? 0;
    const activeChats = activeChatsRes?.activeChats ?? [];

    // ۱) آمار چت‌های در انتظار (از totalWaitingChats + dayData.waitingChats)
    const waitingChatsStats = useMemo(() => {
        const dayData = openChatsDto?.dayData ?? [];
        return {
            count: openChatsDto?.totalWaitingChats ?? 0,
            lastUpdate: new Date(),
            chart: dayData.map((d) => ({
                date: d.date,
                label: faDate(d.date), // تاریخ شمسی با ارقام فارسی
                value: safeNumber(d.waitingChats),
            })),
        };
    }, [openChatsDto]);

    // ۲) آمار چت‌های در حال پاسخ‌گویی
    const operatorActiveStats = useMemo(() => {
        const dayData = openChatsDto?.dayData ?? [];
        return {
            count: activeChats.length,
            lastUpdate: new Date(),
            chart: dayData.map((d) => ({
                date: d.date,
                label: faDate(d.date),
                value: safeNumber(d.openChats),
            })),
        };
    }, [openChatsDto, activeChats.length]);

    // ۳) میانگین زمان انتظار (بک‌اند بر حسب ثانیه → اینجا تبدیل به دقیقه)
    const waitingTimeStats = useMemo(() => {
        const overallSeconds = avgWaitDto?.overallAverage ?? 0;
        const avgMinutes = overallSeconds / 60;

        const chart = (avgWaitDto?.dayAverages ?? []).map((d) => ({
            date: d.date,
            label: faDate(d.date),
            value: safeNumber(d.averageWaitTime) / 60, // ثانیه → دقیقه
        }));

        return {
            avgMinutes,
            chart,
        };
    }, [avgWaitDto]);

    // ۴) آمار رضایت کاربران (میانگین امتیاز – همه‌جا تا دو رقم اعشار)
    const satisfactionStats = useMemo(() => {
        const chart = (avgRatingDto?.dayAverages ?? []).map((d) => ({
            date: d.date,
            label: faDate(d.date),
            value: roundTo(d.averageRating, 2),
        }));

        return {
            averageScore: roundTo(avgRatingDto?.overallAverage ?? 0, 2),
            maxScore: 5,
            chart,
        };
    }, [avgRatingDto]);

// ۵) چت‌های پایان یافته (واقعی، از API)
    const resolvedStats = useMemo(() => {
        const dayData = resolvedChatsRes?.dayData ?? [];

        return {
            count: resolvedChatsRes?.totalResolvedChats ?? 0,
            chart: dayData.map((d) => ({
                date: d.date,
                label: faDate(d.date),               // تاریخ شمسی با MM/DD
                value: safeNumber(d.resolvedChats),  // تعداد چت‌های حل‌شده در آن روز
            })),
        };
    }, [resolvedChatsRes]);

    // ۶) آخرین چت‌های فعال (از activeChats)
    const recentChats = useMemo(() => {
        const sorted = [...activeChats].sort((a, b) => {
            const da = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
            const db = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
            return db - da;
        });
        return sorted.slice(0, 10);
    }, [activeChats]);

    const handleRefresh = () => {
        // تمام کوئری‌های داشبورد اپراتور را ریفرش کن
        queryClient.invalidateQueries({queryKey: ["operator-dashboard"]});
    };

    // وضعیت‌ها از نقشه‌ی مشترک می‌آیند تا AGENT و TIME_OUT دیگر «نامشخص» نشوند.
    const statusTag = (state) => {
        const meta = stateMeta(state);
        return <Tag color={meta.color}>{meta.label}</Tag>;
    };

    return (
        <div className="w-full h-full p-4 md:p-6 space-y-4" dir="rtl">
            {/* هدر داشبورد */}
            <Row align="middle" justify="space-between" className="mb-2">
                <Col>
                    <Space direction="vertical" size={0}>
                        <Text className="text-lg md:text-xl font-semibold">
                            داشبورد اپراتور
                        </Text>
                        <Text type="secondary" className="text-xs md:text-sm">
                            نمای کلی از وضعیت چت‌ها، رضایت کاربران و عملکرد شما
                        </Text>
                    </Space>
                </Col>
                <Col>
                    <Space>
                        <Tooltip title="آخرین بروزرسانی">
                            <Space className="text-xs md:text-sm text-gray-500">
                                <CalendarOutlined/>
                                {faDateWithHour(new Date())}
                            </Space>
                        </Tooltip>
                        <Button
                            icon={<ReloadOutlined/>}
                            onClick={handleRefresh}
                            className="rounded-full"
                        >
                            بروزرسانی
                        </Button>
                    </Space>
                </Col>
            </Row>

            {/* ردیف اول: کارت‌های خلاصه عددی */}
            <Row gutter={[16, 16]}>
                {/* چت‌های در انتظار */}
                <Col xs={24} sm={12} lg={6}>
                    <Card
                        className="shadow-sm rounded-2xl border border-gray-100"
                        bodyStyle={{padding: 16}}
                    >
                        <Space className="w-full" align="start" direction="vertical">
                            <Space className="w-full justify-between">
                                <Space>
                                    <div
                                        className="w-9 h-9 flex items-center justify-center rounded-xl bg-yellow-100 text-yellow-700">
                                        <BellOutlined/>
                                    </div>
                                    <Text className="font-medium text-sm md:text-base">
                                        چت‌های در انتظار
                                    </Text>
                                </Space>
                                <Tag color="gold" className="rounded-full">
                                    WAITING
                                </Tag>
                            </Space>

                            <Text className="text-2xl md:text-3xl font-bold mt-1">
                                {enToFaDigits(waitingChatsStats.count)}
                            </Text>

                            {openChatsDto?.totalOpenChats != null && (
                                <Text type="secondary" className="text-xs">
                                    از مجموع{" "}
                                    {enToFaDigits(openChatsDto.totalOpenChats)} چتِ باز
                                </Text>
                            )}

                            <Space size={4}>
                                <Text type="secondary" className="text-xs">
                                    آخرین بروزرسانی:
                                </Text>
                                <Text type="secondary" className="text-xs">
                                    {faDateWithHour(waitingChatsStats.lastUpdate)}
                                </Text>
                            </Space>

                            <div className="mt-2 h-24">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={waitingChatsStats.chart}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false}/>
                                        <XAxis
                                            dataKey="label"
                                            tickLine={false}
                                            fontSize={11}
                                        />
                                        <YAxis hide/>
                                        <RechartsTooltip
                                            formatter={(value) => [
                                                enToFaDigits(value),
                                                "تعداد چت در انتظار",
                                            ]}
                                            labelFormatter={(label) => `تاریخ: ${label}`}
                                        />
                                        <Bar dataKey="value" radius={[6, 6, 0, 0]}/>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </Space>
                    </Card>
                </Col>

                {/* چت‌های در حال پاسخ‌گویی */}
                <Col xs={24} sm={12} lg={6}>
                    <Card
                        className="shadow-sm rounded-2xl border border-gray-100"
                        bodyStyle={{padding: 16}}
                    >
                        <Space className="w-full" align="start" direction="vertical">
                            <Space className="w-full justify-between">
                                <Space>
                                    <div
                                        className="w-9 h-9 flex items-center justify-center rounded-xl bg-blue-100 text-blue-700">
                                        <SendOutlined/>
                                    </div>
                                    <Text className="font-medium text-sm md:text-base">
                                        چت‌های در حال پاسخ‌گویی
                                    </Text>
                                </Space>
                                <Tag color="blue" className="rounded-full">
                                    OPERATOR
                                </Tag>
                            </Space>

                            <Text className="text-2xl md:text-3xl font-bold mt-1">
                                {enToFaDigits(operatorActiveStats.count)}
                            </Text>

                            <Text type="secondary" className="text-xs">
                                چت‌هایی که هم‌اکنون توسط شما در حال رسیدگی هستند
                            </Text>

                            <div className="mt-2 h-24">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={operatorActiveStats.chart}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false}/>
                                        <XAxis
                                            dataKey="label"
                                            tickLine={false}
                                            fontSize={11}
                                        />
                                        <YAxis hide/>
                                        <RechartsTooltip
                                            formatter={(value) => [
                                                enToFaDigits(value),
                                                "چت‌های فعال",
                                            ]}
                                            labelFormatter={(label) => `تاریخ: ${label}`}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="value"
                                            strokeWidth={2}
                                            dot={{r: 3}}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </Space>
                    </Card>
                </Col>

                {/* میانگین زمان انتظار (بر حسب دقیقه) */}
                <Col xs={24} sm={12} lg={6}>
                    <Card
                        className="shadow-sm rounded-2xl border border-gray-100"
                        bodyStyle={{padding: 16}}
                    >
                        <Space className="w-full" align="start" direction="vertical">
                            <Space className="w-full justify-between">
                                <Space>
                                    <div
                                        className="w-9 h-9 flex items-center justify-center rounded-xl bg-purple-100 text-purple-700">
                                        <ClockIcon/>
                                    </div>
                                    <Text className="font-medium text-sm md:text-base">
                                        میانگین زمان انتظار
                                    </Text>
                                </Space>
                                <Tag color="purple" className="rounded-full">
                                    دقیقه
                                </Tag>
                            </Space>

                            <Text className="text-2xl md:text-3xl font-bold mt-1">
                                {formatDecimalFa(waitingTimeStats.avgMinutes, 2)}
                            </Text>

                            <Text type="secondary" className="text-xs">
                                میانگین زمانی که کاربر قبل از پاسخ اپراتور منتظر می‌ماند
                            </Text>

                            <div className="mt-2 h-24">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={waitingTimeStats.chart}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false}/>
                                        <XAxis
                                            dataKey="label"
                                            tickLine={false}
                                            fontSize={11}
                                        />
                                        <YAxis hide/>
                                        <RechartsTooltip
                                            formatter={(value) => [
                                                formatDecimalFa(value, 2),
                                                "دقیقه",
                                            ]}
                                            labelFormatter={(label) => `تاریخ: ${label}`}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="value"
                                            strokeWidth={2}
                                            dot={{r: 2}}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </Space>
                    </Card>
                </Col>

                {/* چت‌های خوانده‌نشده */}
                <Col xs={24} sm={12} lg={6}>
                    <Card
                        className="shadow-sm rounded-2xl border border-gray-100"
                        bodyStyle={{padding: 16}}
                    >
                        <Space className="w-full" align="start" direction="vertical">
                            <Space className="w-full justify-between">
                                <Space>
                                    <div
                                        className="w-9 h-9 flex items-center justify-center rounded-xl bg-red-100 text-red-600">
                                        <BellOutlined/>
                                    </div>
                                    <Text className="font-medium text-sm md:text-base">
                                        چت‌های خوانده‌نشده
                                    </Text>
                                </Space>
                                <Tag color="red" className="rounded-full">
                                    unread
                                </Tag>
                            </Space>

                            <Text className="text-2xl md:text-3xl font-bold mt-1">
                                {enToFaDigits(unreadCount)}
                            </Text>

                            <Text type="secondary" className="text-xs">
                                چت‌هایی که هنوز باز نکرده‌اید و منتظر پاسخ هستند
                            </Text>
                        </Space>
                    </Card>
                </Col>
            </Row>

            {/* ردیف دوم: رضایت و چت‌های پاسخ داده‌شده */}
            <Row gutter={[16, 16]}>
                {/* رضایت کاربران */}
                <Col xs={24} lg={12}>
                    <Card
                        className="shadow-sm rounded-2xl border border-gray-100"
                        title={
                            <Space>
                                <SmileOutlined className="text-yellow-500"/>
                                <span>رضایت کاربران از پاسخ‌گویی شما</span>
                            </Space>
                        }
                        extra={
                            <Tag color="green" className="rounded-full">
                                میانگین امتیاز:{" "}
                                {formatDecimalFa(satisfactionStats.averageScore, 2)} /{" "}
                                {enToFaDigits(satisfactionStats.maxScore)}
                            </Tag>
                        }
                        bodyStyle={{padding: 16}}
                    >
                        <Row gutter={[16, 16]} align="middle">
                            <Col xs={24} md={10}>
                                <Space direction="vertical">
                                    <Text type="secondary" className="text-xs">
                                        نمودار تغییرات میانگین امتیاز در روزهای اخیر
                                    </Text>
                                    <Text className="text-2xl font-bold">
                                        {formatDecimalFa(satisfactionStats.averageScore, 2)}
                                    </Text>
                                    <Text type="secondary" className="text-xs leading-relaxed">
                                        این نمودار نشان می‌دهد رضایت کاربران در طول روزهای
                                        گذشته چه روندی داشته است.
                                    </Text>
                                </Space>
                            </Col>
                            <Col xs={24} md={14}>
                                <div className="h-40">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={satisfactionStats.chart}>
                                            <CartesianGrid
                                                strokeDasharray="3 3"
                                                vertical={false}
                                            />
                                            <XAxis
                                                dataKey="label"
                                                tickLine={false}
                                                fontSize={12}
                                            />
                                            <YAxis domain={[0, 5]} hide/>
                                            <RechartsTooltip
                                                formatter={(value) => [
                                                    formatDecimalFa(value, 2),
                                                    "امتیاز",
                                                ]}
                                                labelFormatter={(label) => `تاریخ: ${label}`}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="value"
                                                strokeWidth={2}
                                                dot={{r: 3}}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>

                {/* چت‌های پاسخ داده‌شده توسط اپراتور (فعلاً ماک) */}
                <Col xs={24} lg={12}>
                    <Card
                        className="shadow-sm rounded-2xl border border-gray-100"
                        title={
                            <Space>
                                <CheckCircleOutlined className="text-emerald-500"/>
                                <span>چت‌های پایان یافته توسط شما</span>
                            </Space>
                        }
                        extra={
                            <Tag color="blue" className="rounded-full">
                                جمع کل: {enToFaDigits(resolvedStats.count)}
                            </Tag>
                        }
                        bodyStyle={{padding: 16}}
                    >
                        <Row gutter={[16, 16]} align="middle">
                            <Col xs={24} md={10}>
                                <Space direction="vertical">
                                    <Text type="secondary" className="text-xs">
                                        روند چت‌های حل‌شده در روزهای اخیر
                                    </Text>
                                    <Text className="text-2xl font-bold">
                                        {enToFaDigits(resolvedStats.count)}
                                    </Text>
                                    <Button
                                        type="link"
                                        className="p-0"
                                        onClick={() => navigate("/dashboard/chats")}
                                    >
                                        مشاهده چت‌های تمام‌شده
                                    </Button>
                                </Space>
                            </Col>
                            <Col xs={24} md={14}>
                                <div className="h-40">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={resolvedStats.chart}>
                                            <CartesianGrid
                                                strokeDasharray="3 3"
                                                vertical={false}
                                            />
                                            <XAxis
                                                dataKey="label"
                                                tickLine={false}
                                                fontSize={12}
                                            />
                                            <YAxis hide/>
                                            <RechartsTooltip
                                                formatter={(value) => [
                                                    enToFaDigits(value),
                                                    "چت پایان یافته",
                                                ]}
                                                labelFormatter={(label) => `روز: ${label}`}
                                            />
                                            <Bar
                                                dataKey="value"
                                                radius={[8, 8, 0, 0]}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>

            {/* ردیف سوم: آخرین چت‌های فعال */}
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card
                        className="shadow-sm rounded-2xl border border-gray-100"
                        title={
                            <Space>
                                <FileTextOutlined/>
                                <span>آخرین چت‌های فعال</span>
                            </Space>
                        }
                        extra={
                            <Button
                                type="link"
                                onClick={() => navigate("/dashboard/chats")}
                                icon={<SendOutlined/>}
                            >
                                رفتن به صفحه چت‌ها
                            </Button>
                        }
                        bodyStyle={{padding: 0}}
                    >
                        <List
                            itemLayout={isMobile ? "vertical" : "horizontal"}
                            dataSource={recentChats}
                            className="divide-y divide-gray-100"
                            renderItem={(item) => (
                                <List.Item
                                    className="px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
                                    onClick={() => navigate(`/dashboard/chats/${item.id}`)}
                                >
                                    <List.Item.Meta
                                        avatar={
                                            <Avatar className="bg-yellow-500">
                                                {getInitials(item.fullName)}
                                            </Avatar>
                                        }
                                        title={
                                            <Space size={8}>
                                                <Text className="font-medium">
                                                    {item.fullName}
                                                </Text>
                                                {statusTag(item.state)}
                                                {item.unread && (
                                                    <Tag
                                                        color="red"
                                                        className="rounded-full text-xs"
                                                    >
                                                        جدید
                                                    </Tag>
                                                )}
                                            </Space>
                                        }
                                        description={
                                            <Space
                                                direction="vertical"
                                                size={2}
                                                className="w-full"
                                            >
                                                <Text
                                                    className="text-xs md:text-sm text-gray-700"
                                                    ellipsis
                                                >
                                                    {item.lastMessage}
                                                </Text>
                                                <Space
                                                    size={12}
                                                    className="text-[11px] md:text-xs text-gray-400"
                                                >
                                                    <span>
                                                        آخرین فعالیت:{" "}
                                                        {faDateWithHour(item.updatedAt)}
                                                    </span>
                                                    {item.score != null && (
                                                        <span>
                                                            امتیاز:{" "}
                                                            {enToFaDigits(item.score)} /{" "}
                                                            {enToFaDigits(5)}
                                                        </span>
                                                    )}
                                                </Space>
                                            </Space>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

// آیکون ساعت ساده با SVG (برای میانگین زمان انتظار)
function ClockIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            className="w-4 h-4"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/>
            <path
                d="M12 7v5l3 2"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}
